import type {Poster} from '@PosterWhiteboard/poster/poster.class';
import type {SnapshotRecord} from '@PosterWhiteboard/collaboration/poster-op.types';
import {PosterCollaborationConnection} from '@PosterWhiteboard/collaboration/poster-collaboration-connection.class';
import {PosterPresence} from '@PosterWhiteboard/collaboration/poster-presence.class';
import {PosterLiveEdits} from '@PosterWhiteboard/collaboration/poster-live-edits.class';
import {PosterModeType} from '@PosterWhiteboard/poster/poster-mode.class';
import {getUniqueString} from '@Utils/string.util';

/**
 * The tab's op stream and, on top of it, several people editing one poster at once. The op
 * stream (live edits) runs for every editing session the server accepts op envelopes from, and
 * is how the poster is saved. Collaboration adds the realtime connection and the two sides that
 * use it: presence (who is here, their cursors and selections) and the live delivery of the
 * other tabs' envelopes. Started from Poster.onPosterInit and again after a first save gives the
 * poster its id; a tab that is not eligible never connects.
 */
export class PosterCollaboration {
  /** Identifies this tab across presence and edits; one person in two tabs is two sessions. */
  public readonly sessionId = getUniqueString();
  public readonly presence: PosterPresence;
  public readonly liveEdits: PosterLiveEdits;
  #connection: PosterCollaborationConnection | null = null;
  #posterHashId: string | null = null;
  #loadedSeq = 0;

  constructor(private readonly poster: Poster) {
    this.presence = new PosterPresence(poster, this);
    this.liveEdits = new PosterLiveEdits(poster, this);
  }

  /** The live-edit sequence the loaded JSON reflects (from the load response); the log is replayed from here on start. */
  public setLoadedSeq(seq: number): void {
    this.#loadedSeq = seq;
  }

  /** Opens the op stream for a saved poster and, when collaboration is on, joins its channels; leaves the previous poster's first. A poster without an id yet (an unsaved copy) saves the old way first. */
  public start(): void {
    this.#start(this.poster.history.getCurrentSnapshot());
  }

  /**
   * The poster was just saved whole for the first time, so its log is empty and the stream starts
   * from `savedSnapshot`; whatever was committed while that save was in flight becomes the first envelope.
   */
  public startAfterFirstSave(savedSnapshot: SnapshotRecord): void {
    this.#loadedSeq = 0;
    this.#start(savedSnapshot);
    this.liveEdits.onLocalCommit(savedSnapshot, this.poster.history.getCurrentSnapshot());
  }

  #start(confirmedSnapshot: SnapshotRecord): void {
    if (!this.#isOpStreamEligible()) {
      this.stop();
      return;
    }
    if (this.#posterHashId === this.poster.hashedID) {
      return;
    }
    this.stop();
    this.#posterHashId = this.poster.hashedID;
    this.liveEdits.open(this.#loadedSeq, confirmedSnapshot);
    if (this.#isCollaborationEligible()) {
      void this.#join(this.#posterHashId).catch(this.#onJoinFailed.bind(this));
    } else {
      void this.liveEdits.replayLog();
    }
  }

  /** Leaves both channels and closes the connection once presence has said goodbye (closing first would discard that message). */
  public stop(): void {
    const connection = this.#connection;
    this.#connection = null;
    this.#posterHashId = null;
    this.liveEdits.leave();
    void this.presence.leave().finally((): void => {
      connection?.close();
    });
  }

  /** Someone else is in the poster right now, so edits should travel as soon as they are made. */
  public isSharedWithOthers(): boolean {
    return this.liveEdits.isCollaborating() && this.poster.redux.getRemoteSessionCount() > 0;
  }

  #isOpStreamEligible(): boolean {
    const {details} = this.poster.mode;
    const isViewingOlderVersion = details.type === PosterModeType.EDIT && Boolean(details.posterVersionId);
    return (
      window.PMW.isPosterOpStreamSaveEnabled &&
      !this.poster.savePoster.areSavesPaused() &&
      window.PMW.getUserId() !== 'notloggedin' &&
      this.poster.id !== '' &&
      this.poster.hashedID !== '' &&
      !isViewingOlderVersion &&
      !this.poster.mode.isGeneration() &&
      !this.poster.mode.isWebpage() &&
      !this.poster.mode.isRegen()
    );
  }

  #isCollaborationEligible(): boolean {
    return window.PMW.isPosterCollaborationEnabled && window.PMW.streamTransport !== 'off';
  }

  async #join(posterHashId: string): Promise<void> {
    const connection = new PosterCollaborationConnection(posterHashId);
    await connection.open();
    if (this.#posterHashId !== posterHashId) {
      connection.close();
      return;
    }
    this.#connection = connection;
    this.presence.join(connection);
    await this.liveEdits.join(connection);
  }

  /** Access to the channels was refused or lost; the tab keeps saving through its op stream, edits alone, and still shows what the log holds. */
  #onJoinFailed(error: unknown): void {
    window.PMW.log('poster-collaboration: could not join the poster channels', error);
    void this.liveEdits.replayLog();
  }
}
